import { fetchAuthSession, signOut } from "aws-amplify/auth";
import * as React from "react";

import { Env } from "@/shared/environment";
import {
  type BearerTokenVendor,
  HttpClient,
  robotoHeaders,
} from "@/shared/http";

import { HttpClientContext } from "./HttpClientContext";

async function cognitoIdToken(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

interface HttpClientProviderProps {
  /** Defaults to the Cognito ID token. */
  bearerTokenVendor?: BearerTokenVendor;
  /** Called when no bearer token can be obtained. Defaults to Cognito sign-out. */
  onAuthFailure?: () => Promise<void>;
  /** Whether to retry transient failures. Defaults to true. */
  retry?: boolean;
}

export function HttpClientProvider({
  bearerTokenVendor = cognitoIdToken,
  onAuthFailure = signOut,
  retry = true,
  children,
}: React.PropsWithChildren<HttpClientProviderProps>) {
  const [resourceOwnerId, setResourceOwnerId] = React.useState<
    string | undefined
  >(undefined);

  const httpClientContext = React.useMemo(() => {
    return {
      httpClient: new HttpClient({
        // The active org is sent as both org headers, because routes are split
        // between two auth rules that read different ones: REQUIRES_OWNER_ORG
        // reads X-Roboto-Resource-Owner-Id, REQUIRES_CALLER_ORG reads
        // X-Roboto-Org-Id. Either header alone leaves the other rule's routes
        // failing with RobotoNoOrgProvidedException for anyone in more than one
        // org — a single-org caller never noticed, because the service falls
        // back to their only org. Callers that need a *different* org for one
        // request still pass `robotoHeaders({ orgId })` themselves; per-request
        // headers merge over these (see HttpClient's #mergeWithBaseHeaders).
        baseHeaders: robotoHeaders({
          orgId: resourceOwnerId,
          resourceOwnerId,
        }),
        baseUrl: new URL(Env.robotoServiceBaseUrl),
        bearerTokenVendor,
        onAuthFailure,
        retry,
      }),
      setResourceOwnerId,
    };
  }, [resourceOwnerId, bearerTokenVendor, onAuthFailure, retry]);

  return (
    <HttpClientContext.Provider value={httpClientContext}>
      {children}
    </HttpClientContext.Provider>
  );
}
